import React, { useState, useContext, useRef, useEffect } from 'react';
import { newUid, deep_copy } from '@/util';
import { WidgetHeader } from './WidgetHeader';
import { useTranslation } from 'react-i18next';
import { encode } from '@/base64';
import { InputScore, Button, Switch, IconButton } from '@teo/components';
import { TrashIcon, Plus } from '@teo/components/icons';
import UploadDropzoneImage from '@/components/UploadDropzoneImage/UploadDropzoneImage';
import { uploadImage } from '@/query/documents';
import { QuestionSection } from '../QuestionSection';
import retry from 'async-await-retry';
import WidgetCompetencies from './widgetOptions/WidgetCompetencies';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import { LibraryModal } from '@/components/LibraryModal/LibraryModal';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const MIN_SIZE = 0.04;

const createArea = () => ({
  uid: newUid(20),
  x: 0,
  y: 0,
  w: 0.11,
  h: 0.11,
});

export const initialShowOnPicture = () => ({
  score: 1,
  vraag: '',
  type: 'ShowOnPicture',
  image: null,
  orderedMarkers: false,
  labels: [createArea()],
});

export const ShowOnPictureEdit = ({
  state,
  index,
  onModified = undefined,
  setStateFn = undefined,
  lessonId,
}) => {
  const { setShowNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const [img, setImg] = useState(state.image);
  const [labels, setLabels] = useState(
    state?.labels?.length ? deep_copy(state.labels) : [createArea()]
  );
  const [selectedLabelUid, setSelectedLabelUid] = useState(
    state?.labels?.[0]?.uid || null
  );

  const [titleWidget, setTitleWidget] = useState(
    state?.titleWidget ? state.titleWidget : t('widgets.type.show_on_picture')
  );

  const [orderedMarkers, setOrderedMarkers] = useState(
    state.orderedMarkers !== undefined ? state.orderedMarkers : false
  );

  const imageWrapRef = useRef(null);

  const [competencies, setCompetencies] = useState([]);
  const [modified, setModified] = useState(newUid(20));
  const [immediateFeedback, setImmediateFeedback] = useState(
    !!state.immediateFeedback
  );
  const [showAnswer, setShowAnswer] = useState(
    state.showAnswer !== undefined ? state.showAnswer : false
  );
  const [score, setScore] = useState(state.score || 0);
  const [questionVraag, setQuestionVraag] = useState(
    state?.vraag ? state?.vraag : null
  );
  const [excludeScore, setExcludeScore] = useState(
    state.excludeScore !== undefined ? state.excludeScore : false
  );
  const [antwoordVraag, setAntwoordVraag] = useState(
    state?.antwoord ? state?.antwoord : null
  );

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isOpenLibraryModal, setIsOpenLibraryModal] = useState(false);

  const dragStateRef = useRef(null);

  const getState = async () => {
    const newState = deep_copy(state);

    newState.immediateFeedback = immediateFeedback;

    await retry(() => {
      const questionNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-question .question_optional`
      );
      if (questionNode) {
        newState.vraag = questionNode.innerHTML;
        return Promise.resolve();
      }
      return Promise.reject();
    });

    await retry(() => {
      const answerNode = document.querySelector(
        `#${CSS.escape(state.uid)} .wg-answer .question_optional`
      );
      if (answerNode) {
        newState.antwoord = answerNode.innerHTML;
        return Promise.resolve();
      }
      return Promise.reject();
    });

    newState.showAnswer = showAnswer;
    newState.score = score;
    newState.titleWidget = titleWidget;
    newState.image = img;
    newState.orderedMarkers = orderedMarkers;
    newState.labels = deep_copy(labels);
    newState.competences = competencies;
    newState.excludeScore = excludeScore;

    if (state?.group) {
      newState.titleWidgetGroup = state?.titleWidgetGroup;
    }

    return newState;
  };

  setStateFn && setStateFn(getState);

  useEffect(() => {
    onModified && onModified();
  }, [modified, onModified]);

  useEffect(() => {
    setImageLoaded(false);
  }, [img]);

  const getContainerMetrics = () => {
    const rect = imageWrapRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return rect;
  };

  const updateLabel = (uid, updater) => {
    setLabels((prev) =>
      prev.map((label) => {
        if (label.uid !== uid) return label;
        const next =
          typeof updater === 'function'
            ? updater(label)
            : { ...label, ...updater };

        return {
          ...next,
          x: clamp(next.x, 0, 1 - next.w),
          y: clamp(next.y, 0, 1 - next.h),
          w: clamp(next.w, MIN_SIZE, 1),
          h: clamp(next.h, MIN_SIZE, 1),
        };
      })
    );
    setModified(newUid(20));
  };

  const addArea = () => {
    const newArea = createArea();
    setLabels((prev) => [...prev, newArea]);
    setSelectedLabelUid(newArea.uid);
    setModified(newUid(20));
  };

  const removeArea = (uid) => {
    setLabels((prev) => {
      const next = prev.filter((item) => item.uid !== uid);
      if (!next.length) {
        const fallback = createArea();
        setSelectedLabelUid(fallback.uid);
        return [fallback];
      }
      if (selectedLabelUid === uid) {
        setSelectedLabelUid(next[0].uid);
      }
      return next;
    });
    setModified(newUid(20));
  };

  const startInteraction = (e, label, mode, handle = null) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = getContainerMetrics();
    if (!rect) return;

    setSelectedLabelUid(label.uid);

    dragStateRef.current = {
      uid: label.uid,
      mode,
      handle,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: label.x,
      startY: label.y,
      startW: label.w,
      startH: label.h,
      rect,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragStateRef.current;
      if (!drag) return;

      const dx = (e.clientX - drag.startClientX) / drag.rect.width;
      const dy = (e.clientY - drag.startClientY) / drag.rect.height;

      if (drag.mode === 'move') {
        updateLabel(drag.uid, {
          x: clamp(drag.startX + dx, 0, 1 - drag.startW),
          y: clamp(drag.startY + dy, 0, 1 - drag.startH),
          w: drag.startW,
          h: drag.startH,
        });
        return;
      }

      let nextX = drag.startX;
      let nextY = drag.startY;
      let nextW = drag.startW;
      let nextH = drag.startH;

      if (drag.handle.includes('e')) {
        nextW = clamp(drag.startW + dx, MIN_SIZE, 1 - drag.startX);
      }
      if (drag.handle.includes('s')) {
        nextH = clamp(drag.startH + dy, MIN_SIZE, 1 - drag.startY);
      }
      if (drag.handle.includes('w')) {
        const right = drag.startX + drag.startW;
        nextX = clamp(drag.startX + dx, 0, right - MIN_SIZE);
        nextW = clamp(right - nextX, MIN_SIZE, 1);
      }
      if (drag.handle.includes('n')) {
        const bottom = drag.startY + drag.startH;
        nextY = clamp(drag.startY + dy, 0, bottom - MIN_SIZE);
        nextH = clamp(bottom - nextY, MIN_SIZE, 1);
      }

      updateLabel(drag.uid, {
        x: nextX,
        y: nextY,
        w: nextW,
        h: nextH,
      });
    };

    const onUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  return (
    <div
      data-open="SOW"
      className="flex w-full flex-col gap-4 px-4 py-4"
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
      id={state.uid}
    >
      <WidgetHeader
        index={index}
        titleWidget={titleWidget}
        setTitleWidget={setTitleWidget}
      />

      <div className="wg-question">
        <QuestionSection questionVraag={questionVraag} />
      </div>

      <div className="my-4 flex gap-3">
        <Switch
          checked={showAnswer}
          onChange={(isCheck) => setShowAnswer(isCheck)}
        />
        <div className="mr-4">
          {t('widgets.edit.matrix_question.explain_after_submit')}
        </div>
      </div>

      <div className={!showAnswer ? 'hidden' : ''}>
        <div className="wg-answer">
          <QuestionSection questionVraag={antwoordVraag} exampleAnswer />
        </div>
      </div>

      {!img ? (
        <div className="flex flex-col items-center justify-center rounded-lg bg-grey-02 px-3 py-8">
          <div className="flex items-center">
            <img src="/images/icons/image.svg" alt="image" className="mr-3" />
            <span className="text-base font-black font-medium">
              {t('widgets.type.add_image')}
            </span>
          </div>

          <div className="my-4 text-sm font-normal text-grey-08">
            {t('widgets.type.uploade_or_select')}
          </div>

          <Button
            variant="outline"
            size="md"
            onClick={() => {
              setIsOpenLibraryModal(true);
            }}
            className="bg-grey-02"
          >
            {t('widgets.type.select_image')}
          </Button>
        </div>
      ) : (
        <>
          <div className="relative w-full rounded-md" contentEditable="false">
            <div className="mb-3 flex flex-row items-center">
              <Switch
                checked={orderedMarkers}
                onChange={(isCheck) => {
                  setOrderedMarkers(isCheck);
                  setModified(newUid(20));
                }}
              />
              <div className="pl-4">
                {t('widgets.show_onpicture.use_ordered_markers')}
              </div>
            </div>
            <div
              ref={imageWrapRef}
              className="relative w-full overflow-hidden rounded-md"
            >
              <img
                src={img}
                id={state.uid + '_img'}
                className="pointer-events-none block w-full select-none rounded-md"
                draggable="false"
                alt=""
                onLoad={() => setImageLoaded(true)}
              />

              {imageLoaded &&
                labels.map((label, index) => {
                  return (
                    <div
                      key={label.uid}
                      className={`group absolute rounded-[4px] border-2  border-secondary-04 bg-[#3257FC]/25`}
                      style={{
                        left: `${label.x * 100}%`,
                        top: `${label.y * 100}%`,
                        width: `${label.w * 100}%`,
                        height: `${label.h * 100}%`,
                      }}
                      onMouseDown={(e) => startInteraction(e, label, 'move')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLabelUid(label.uid);
                      }}
                    >
                      {orderedMarkers && (
                        <div className="bg-primary pointer-events-none absolute left-1/2 top-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center text-xl font-bold text-white">
                          {index + 1}
                        </div>
                      )}
                      <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 opacity-0 transition group-hover:opacity-100">
                        <IconButton
                          Icon={TrashIcon}
                          variant="secondary"
                          size="sm"
                          className="!text-error-04"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeArea(label.uid);
                          }}
                        />
                      </div>

                      {[
                        {
                          key: 'n',
                          className:
                            'left-1/2 top-0 h-2 w-6 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize',
                        },
                        {
                          key: 's',
                          className:
                            'left-1/2 bottom-0 h-2 w-6 -translate-x-1/2 translate-y-1/2 cursor-ns-resize',
                        },
                        {
                          key: 'w',
                          className:
                            'left-0 top-1/2 h-6 w-2 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize',
                        },
                        {
                          key: 'e',
                          className:
                            'right-0 top-1/2 h-6 w-2 translate-x-1/2 -translate-y-1/2 cursor-ew-resize',
                        },
                      ].map((handle) => (
                        <div
                          key={handle.key}
                          className={`absolute z-20 rounded bg-[#3257FC] ${handle.className}`}
                          onMouseDown={(e) =>
                            startInteraction(e, label, 'resize', handle.key)
                          }
                        />
                      ))}
                    </div>
                  );
                })}
            </div>

            {imageLoaded && (
              <div className="pointer-events-none absolute bottom-6 right-6">
                <div className="pointer-events-auto">
                  <div
                    onClick={addArea}
                    className="flex cursor-pointer items-center gap-2 rounded-full bg-white py-2 px-3 text-sm font-medium text-grey-09 shadow-[0_0_12px_-6px_black]"
                  >
                    <Plus className="h-6 w-6 text-primary-05" />
                    {t('widgets.show_onpicture.add_area')}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="mt-4 flex flex-row items-center border-b border-t border-grey-02 py-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsOpenLibraryModal(true);
                }}
              >
                {t('widgets.widgets_edit.replace_image')}
              </Button>

              {!state?.group && (
                <>
                  <div className="ml-4 flex w-full flex-row items-center border-l-2 border-grey-02 pl-4 sm:mt-0 sm:w-auto">
                    <Switch
                      checked={excludeScore}
                      onChange={(isCheck) => {
                        setExcludeScore(isCheck);
                        if (isCheck) {
                          setScore(0);
                        }
                      }}
                    />
                    <div className="pl-4">
                      {t('widgets.edit.exclude_score')}
                    </div>
                  </div>

                  <div className="mr-2 ml-4 border-l-2 border-grey-02 pl-4">
                    {t('widgets.widgets_edit.max_score')}
                  </div>

                  <InputScore
                    value={score}
                    minScore={0}
                    onChange={(value) => setScore(value)}
                    disabled={excludeScore}
                  />
                </>
              )}
            </div>
          </div>
        </>
      )}

      <WidgetCompetencies
        state={state}
        setCompetencies={setCompetencies}
        competencies={competencies}
        lessonId={lessonId}
      />

      {isOpenLibraryModal && (
        <LibraryModal
          setIsOpenLibraryModal={setIsOpenLibraryModal}
          isOpenLibraryModal={isOpenLibraryModal}
          lessonPage={false}
          setUploadFile={(img) => {
            setImg(img);
            setModified(newUid(20));
          }}
          onSelectImage={(img) => {
            setImg(img);
            setModified(newUid(20));
          }}
        />
      )}
    </div>
  );
};
